import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppNavigate } from '../contexts/PwaNavContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../utils/api';
import FirebaseAuthModal from '../components/FirebaseAuthModal';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import type { Customer } from '../contexts/CustomerAuthContext';
import './LoginClientPage.css';

interface InviterData {
  displayName: string;
  stamps: { id: string; name: string }[];
  level: string;
}

export default function LoginClientPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useCustomerAuth();
  const [searchParams] = useSearchParams();
  const navigate = useAppNavigate();
  const refCode = searchParams.get('ref');

  const [showAuth, setShowAuth] = useState(false);
  const [inviter, setInviter] = useState<InviterData | null>(null);
  const [inviterLoading, setInviterLoading] = useState(false);

  // Store return URL so we can navigate back after login
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (!isAuthenticated) return;
    // If there's a pending ref code, process it before redirecting
    const pendingRef = refCode || sessionStorage.getItem('olso_pending_ref');
    if (pendingRef) {
      sessionStorage.removeItem('olso_pending_ref');
      const token = localStorage.getItem('olso_customer_token');
      if (token) {
        const base = API_URL || '';
        axios.post(
          `${base}/api/friends/join`,
          { referralCode: pendingRef },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }
    }
    navigate(returnTo || '/');
  }, [isAuthenticated, navigate, returnTo, refCode]);

  useEffect(() => {
    if (!refCode) return;
    sessionStorage.setItem('olso_pending_ref', refCode);

    const fetchInviter = async () => {
      setInviterLoading(true);
      try {
        const base = API_URL || '';
        const { data } = await axios.get(`${base}/api/friends/inviter/${refCode}`);
        setInviter(data);
      } catch {
        // ignore
      } finally {
        setInviterLoading(false);
      }
    };
    fetchInviter();
  }, [refCode]);

  const handleAuthSuccess = async (_customer: Customer, token: string) => {
    const pendingRef = sessionStorage.getItem('olso_pending_ref');
    if (pendingRef && token) {
      sessionStorage.removeItem('olso_pending_ref');
      try {
        const base = API_URL || '';
        await axios.post(
          `${base}/api/friends/join`,
          { referralCode: pendingRef },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        // ignore
      }
    }
    navigate(returnTo || '/');
  };

  const getInitials = (name: string) =>
    name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="lc-container">
      <button
        className="lc-close-btn"
        onClick={() => navigate('/')}
        aria-label="Close"
      >
        ×
      </button>
      <div className="lc-hero">
        <div className="lc-hero-logo">
          <img src="/logo-olso.png" alt="OLSO" className="lc-hero-logo-img" />
          <div className="lc-hero-logo-text">
            <div className="lc-hero-logo-sub">{t('home.brandSub')}</div>
          </div>
        </div>
        <h1 className="lc-hero-headline">
          {t('loginClient.headline1')}<br />
          <span>{t('loginClient.headline2')}</span><br />
          {t('loginClient.headline3')}
        </h1>
        <p className="lc-hero-sub">{t('loginClient.heroSub')}</p>
      </div>

      {refCode && !inviterLoading && inviter && (
        <div className="lc-invite-card">
          <div className="lc-invite-avatar">{getInitials(inviter.displayName)}</div>
          <div className="lc-invite-info">
            <div className="lc-invite-label">{t('loginClient.invitedBy')}</div>
            <div className="lc-invite-name">{inviter.displayName}</div>
            <div className="lc-invite-stamps">
              {inviter.stamps.slice(0, 3).map((stamp) => (
                <div key={stamp.id} className="lc-invite-stamp"></div>
              ))}
              {inviter.stamps.length > 3 && (
                <div className="lc-invite-stamp lc-invite-stamp--more">+{inviter.stamps.length - 3}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="lc-features">
        <div className="lc-feature">
          <div className="lc-feature-icon lc-feature-icon--amber"></div>
          <div className="lc-feature-text">
            <div className="lc-feature-title">{t('loginClient.feature1Title')}</div>
            <div className="lc-feature-desc">{t('loginClient.feature1Desc')}</div>
          </div>
        </div>
        <div className="lc-feature">
          <div className="lc-feature-icon lc-feature-icon--green"></div>
          <div className="lc-feature-text">
            <div className="lc-feature-title">{t('loginClient.feature2Title')}</div>
            <div className="lc-feature-desc">{t('loginClient.feature2Desc')}</div>
          </div>
        </div>
        <div className="lc-feature">
          <div className="lc-feature-icon lc-feature-icon--purple"></div>
          <div className="lc-feature-text">
            <div className="lc-feature-title">{t('loginClient.feature3Title')}</div>
            <div className="lc-feature-desc">{t('loginClient.feature3Desc')}</div>
          </div>
        </div>
      </div>

      {refCode && (
        <div className="lc-bonus-badge">
          <div className="lc-bonus-icon"></div>
          <div className="lc-bonus-text">
            <div className="lc-bonus-title">{t('loginClient.bonusTitle', { points: 50 })}</div>
            <div className="lc-bonus-sub">
              {inviter
                ? t('loginClient.bonusSubFriend', { name: inviter.displayName })
                : t('loginClient.bonusSub')}
            </div>
          </div>
        </div>
      )}

      <div className="lc-auth">
        <div className="lc-auth-title">{t('loginClient.authTitle')}</div>

        <button className="lc-auth-btn lc-auth-btn--primary" onClick={() => setShowAuth(true)}>
          <span className="lc-auth-btn-icon">
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </span>
          {t('loginClient.googleBtn')}
        </button>

        <button className="lc-auth-btn" onClick={() => setShowAuth(true)}>
          <span className="lc-auth-btn-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
          </span>
          {t('loginClient.appleBtn')}
        </button>

        <div className="lc-auth-divider">
          <div className="lc-auth-divider-line" />
          <div className="lc-auth-divider-text">{t('common.or')}</div>
          <div className="lc-auth-divider-line" />
        </div>

        <button className="lc-auth-btn" onClick={() => setShowAuth(true)}>
          <span className="lc-auth-btn-icon"></span>
          {t('loginClient.phoneBtn')}
        </button>

        <div className="lc-auth-note">{t('loginClient.authNote')}</div>
      </div>

      {showAuth && (
        <FirebaseAuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
}
